import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTitle, usePosting } from '../../Hooks';
import { Modal, NotFound } from '../../components';
import { PostingJobEdit, SetLoading } from '../../Classes';
import { Header, Table, Button, Field, Input, Message, Toast, Label, Groupper } from 'react-frontier';
import { getPayRateTypeLocale } from '../../Util';
import { bindClick, bindFormChange, addCommas } from '@tempu/components/Util';
import { Dropdown, Icon } from 'semantic-ui-react';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import MDEditor, { commands } from '@uiw/react-md-editor/nohighlight';
import remarkGfm from 'remark-gfm';
import BusinessAccess from '../../BusinessAccess';
import moment from 'moment';
import classNames from 'classnames';
import Validator from '@tempu/components/Validator';
import API from '../../API';

enum Modals{
	EDIT = 1,
	PUBLISH = 2,
	INSTRUCTIONS = 4
}

var PostingJob = ()=>{
	var params = useParams<{ bid: string, pid: string, jid: string }>();
	var posting_id = parseInt(params?.pid);
	var { setTitle } = useTitle();
	var { hasAccess } = usePosting(posting_id);
	var job_id = parseInt(params?.jid);
	var [loadError, setLoadError] = useState<string>(null);
	var [job, setJob] = useState<PostingJobEdit>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [jobForm, setJobForm] = useState<PostingJobEdit>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(!job_id || Number.isNaN(job_id)) return;
		if(!hasAccess(BusinessAccess.POSTINGS.VIEW_JOB)) return;

		API.getJob(job_id, true, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setTitle(res.data.job_name);
			setJob(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando los datos del puesto. (LCL-1)');
		});
	}, []);

	var access_check = useMemo(()=>{
		return hasAccess(BusinessAccess.POSTINGS.VIEW_JOB)
	}, []);

	if(!job_id || Number.isNaN(job_id)) return <NotFound />
	if(!access_check){
		return <Header text='Sin acceso' subtext='No tienes acceso a esta página' />
	}
	if(loadError){
		return <Header text='Error' subtext={loadError} />
	}

	if(!job){
		return <Header loading text='Cargando datos' />;
	}

	var openEditModal = ()=>{
		setJobForm(job);
		setShownModal(Modals.EDIT);
	}

	var changePublish = async(setLoading: (l: boolean)=>void)=>{
		setLoading(true);
		API.publishJob(job_id, !job.published).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Puesto actualizado');
			var j = {...job};
			j.published = !j.published;
			setJob(j);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al modificar el puesto. (PEE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var edit = async(setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(jobForm, {
			job_name: [{
				rule: 'minLength', params: [3], label: 'Nombre de puesto'
			}],
			job_count: [{
				rule: 'number', label: 'Vacantes'
			}],
			rate_type: [{
				rule: 'number', label: 'Frecuencia'
			}],
			pay_rate: [{
				rule: 'number', label: 'Sueldo'
			}],
			description: [{
				rule: 'minLength', params: [3], label: 'Descripción'
			}],
			date_start: [{
				rule: 'empty', label: 'Fecha inicio'
			}],
			date_end: [{
				rule: 'empty', label: 'Fecha fin'
			}],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		if(jobForm.date_end < jobForm.date_start) return setModalPrompts(['La fecha fin debe ser mayor a la fecha inicio.']);
		setLoading(true);
		API.editPostingJob(job.job_id, jobForm).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Puesto actualizado!');
			setJob(jobForm);
		}).catch(err=>{
			setModalPrompts(['Hubo un error al actualizar el puesto. (JBE-1)']);
		}).finally(()=>{
			setShownModal(null);
			setLoading(false);
		});
	}

	var openInstructionsModal = ()=>{
		setJobForm(job);
		setShownModal(Modals.INSTRUCTIONS);
	}

	var onEditForm = bindFormChange(jobForm, setJobForm);

	return <div>
		<div className="tp job card">
			<div className="header">
				<div className="left">
					<div className="title">{job.job_name}</div>
					<div className="pay">
						${addCommas(job.pay_rate)}
						<span className="rate"> / {getPayRateTypeLocale(job.rate_type)}</span>
					</div>
				</div>
				<div className="right">
					<Button text='Editar' className='gray' iconName={'pen'} onClick={openEditModal} />
				</div>
			</div>
			<div className="labels">
				<Label color={job.published ? 'green' : 'blue'}>
					{job.published ? 'Publicado' : 'No publicado'}
				</Label>
				<Label iconName='users'>
					{job.users?.length || 0} / {job.job_count}
				</Label>
			</div>
			<div className='labels'>
				<Label>
					<Icon name='calendar' />
					{moment.unix(job.date_start).format('DD/MMM/YY HH:mm')}
				</Label>
				<Label>
					<Icon name='calendar' />
					{moment.unix(job.date_end).format('DD/MMM/YY HH:mm')}
				</Label>
			</div>
			<div className="description">
				{job.description}
			</div>
		</div>
		<Groupper style={{ marginBottom: 15, marginTop: 15 }}>
			<div className="head">
				<div className="left">Instrucciones</div>
				{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
					<Button text='Editar' iconName='pen' size='tiny' onClick={openInstructionsModal} />
				)}
			</div>
			<div className="content">
				{job.instructions ? (
					<ReactMarkdown linkTarget={'_blank'} remarkPlugins={[remarkGfm]} children={job.instructions} />
				) : (
					<Header iconName='info' text={'Sin instrucciones'} subtext={'Agrega instrucciones al puesto.'} size='small' actions={hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
						<Button text='Editar' onClick={openInstructionsModal} />
					)} />
				)}
			</div>
		</Groupper>
		<Message style={{ maxWidth: 500, margin: '10px auto', marginTop: 10 }} type={job.published ? 'info' : 'orange'} centered>
			{!job.published ? <>
				<div className="header">No publicado</div>
				Este puesto no se encuentra publicado.
				<div style={{ marginTop: 15 }}>
					<Button text='Publicar' color='black' onClick={() => setShownModal(Modals.PUBLISH)}/>
				</div>
			</> : <>
				<div className="header">Publicado</div>
				Este puesto se encuentra publicado.
				<div style={{ marginTop: 15 }}>
					<Button text='Despublicar' color='black' onClick={() => setShownModal(Modals.PUBLISH)}/>
				</div>
			</>}
		</Message>
		<Table
			details
			striped
			style={{ margin: 'auto', maxWidth: 400 }}
			title='Información'
			titleSize='small'
			data={[
				['ID', job.job_id],
				['Creador', job.user_creator_name],
				['Creación', moment.unix(job.date_created).format('DD/MMM/YY HH:mm')],
				job.deleted ? ['Eliminado', moment.unix(job.date_deleted).format('DD/MMM/YY HH:mm')] : null,
				job.deleted ? ['Elimino', job.user_deleted_name] : null,
			].filter(row => row !== null)}
		/>
		<Modal open={shownModal === Modals.EDIT} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>Editar puesto</Modal.Header>
			{!!jobForm && <Modal.Content>
				<Field amount={2}>
					<Input label='Nombre' value={jobForm.job_name} onChange={onEditForm('job_name')} placeholder='Ingresa el nombre del puesto.'/>
					<Input label='Vacantes' value={jobForm.job_count} onChange={onEditForm('job_count')}/>
				</Field>
				<Field amount={2}>
					<Input label='Sueldo' placeholder='Ingesa el sueldo' value={jobForm.pay_rate} onChange={onEditForm('pay_rate')}/>
					<Field label='Frecuencia'>
						<Dropdown selection value={jobForm.rate_type} onChange={onEditForm('rate_type', true)} placeholder='Selecciona la frecuencia' options={[
							{ value: 10, text: 'Hora' },
							{ value: 20, text: 'Diario' },
							{ value: 1, text: 'Fijo' },
						]} />
					</Field>
				</Field>
				<Field amount={2}>
					<Input 
						label='Fecha inicio' 
						style={{ width: '100%'}} 
						value={jobForm.date_start} 
						onChange={onEditForm('date_start')} 
						calendar={{ 
							date: jobForm.date_start || moment().unix(), 
							mode: 'datetime', 
						}} />
					<Input 
						label='Fecha fin' 
						style={{ width: '100%'}} 
						value={jobForm.date_end} 
						onChange={onEditForm('date_end')} 
						calendar={{ 
							date: jobForm.date_end || moment().unix(), 
							mode: 'datetime', 
						}} />
				</Field>
				<Input label='Descripción' value={jobForm.description} onChange={onEditForm('description')} textarea/>
				<Message list={modalPrompts} type='error' />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text='Guardar' color='black' onClick={edit}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.PUBLISH} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>{job.published ? 'Despublicar' : 'Publicar'} puesto</Modal.Header>
			<Modal.Content>
				<Header text={`${job.published ? 'Despublicar' : 'Publicar'} puesto`} subtext={!job.published ? 'Se permitirá que los usuarios se postulen para el puesto.' : 'Se detendrá la postulación de usuarios para el puesto.'} />
				<Table
					details
					title='Puesto'
					data={[
						['ID', job.job_id],
						['Nombre', job.job_name],
						['Estatus', <div className={classNames("fr label", {
							green: job.published && !job.deleted,
							red: job.deleted,
							yellow: !job.published && !job.deleted
						})}>
							{job.deleted ? 'Eliminado' : job.published ? 'Publicado' : 'No publicado'}
					</div>]
					]}
				/>
				<Message list={modalPrompts} type='error' />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
				<Button text={job.published ? 'Despublicar' : 'Publicar'} color='black' onClick={changePublish} />
			</Modal.Actions>
		</Modal>
		{hasAccess(BusinessAccess.POSTINGS.EDIT_POSTING) && (
			<Modal open={shownModal===Modals.INSTRUCTIONS} onClose={bindClick(setShownModal)} size='small'>
				<Modal.Header>Editar instrucciones</Modal.Header>
				{jobForm && <Modal.Content>
					<Field label='Descripción'>
						<MDEditor 
							preview='edit'
							style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}
							value={jobForm.instructions} 
							onChange={onEditForm('instructions')} 
							height={400}
							data-color-mode='light'
							extraCommands={[{
								...commands.codeEdit,
								icon: <Icon name='pencil' style={{ margin: 0, fontSize: 18 }} />
							}, {
								...commands.codePreview,
								icon: <Icon name='eye' style={{ margin: 0, fontSize: 18 }} />
							}]}
							commands={[
								commands.bold,
								commands.italic,
								commands.strikethrough,
								commands.divider,
								commands.hr,
								commands.quote,
								commands.code,
								commands.divider,
								commands.unorderedListCommand,
								commands.orderedListCommand,
							]}
						/>	
					</Field>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Guardar' color='black' onClick={edit}/>
				</Modal.Actions>
			</Modal>
		)}
	</div>
}

export default PostingJob;